import React, { useState } from 'react';
import DatePicker from 'antd/lib/date-picker';
import moment from 'moment';
import FormWizard from '../FormWizard';
import { request } from '../../services/utilities';
import { notifyError, notifySuccess } from '../../services/notify';

const emptyScan = { date: '', lining: '', right: '', left: '' };

const MonitoringSheetDetailsForm = ({ closeModal, ivf, onSaved }) => {
	const existing = ivf?.monitoringSheetDetails || {};

	const [scans, setScans] = useState(
		existing.baselineScans && existing.baselineScans.length ? existing.baselineScans : [emptyScan, emptyScan]
	);
	const [semen, setSemen] = useState({
		saDate: '',
		ds: '',
		code: '',
		orderedStored: '',
		mesaPesaTesa: '',
		...existing.semen,
	});
	const [medications, setMedications] = useState({
		aspirin: false,
		dxmPrednisolone: false,
		metformin: false,
		cabergolin: false,
		...existing.medications,
	});
	const [consent, setConsent] = useState({
		givenToPatient: false,
		givenDate: '',
		returnedByPatient: false,
		returnedDate: '',
		...existing.consent,
	});
	const [saving, setSaving] = useState(false);

	const updateScan = (index, field, value) => {
		const next = scans.map((scan, i) => (i === index ? { ...scan, [field]: value } : scan));
		setScans(next);
	};

	const onSubmit = async () => {
		const data = {
			baselineScans: scans,
			semen,
			medications,
			consent,
		};

		try {
			setSaving(true);
			const url = `ivf/${ivf.id}/monitoring-sheet`;
			const rs = await request(url, 'PATCH', true, data);
			if (rs.success) {
				notifySuccess('Monitoring sheet details saved');
				onSaved(data);
				closeModal();
			} else {
				notifyError(rs.message || 'Failed to save monitoring sheet details');
			}
		} catch (error) {
			console.log(error);
			notifyError('Failed to save monitoring sheet details');
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="onboarding-modal modal fade animated show" role="dialog" style={{ display: 'block' }}>
			<div className="modal-dialog modal-content modal-centered modal-scroll" style={{ maxWidth: '1024px' }}>
				<div className="modal-content text-center">
					<button aria-label="Close" className="close" type="button" onClick={closeModal}>
						<span className="os-icon os-icon-close"></span>
					</button>
					<div className="onboarding-content with-gradient">
						<h4 className="onboarding-title">Monitoring Sheet Details</h4>
						<div className="form-block element-box text-left">
							<FormWizard initialValues={{}} onSubmit={onSubmit} btnClasses="modal-footer buttons-on-right">
								<FormWizard.Page>
									<h6>Baseline Scan(s)</h6>
									{scans.map((scan, i) => (
										<div className="row align-items-center" key={i}>
											<div className="col-sm-3">
												<div className="form-group">
													<label>Scan {i + 1} Date</label>
													<DatePicker
														className="form-control"
														value={scan.date ? moment(scan.date) : null}
														onChange={date => updateScan(i, 'date', date ? date.format('YYYY-MM-DD') : '')}
													/>
												</div>
											</div>
											<div className="col-sm-3">
												<div className="form-group">
													<label>Lining</label>
													<input
														className="form-control"
														type="text"
														value={scan.lining}
														onChange={e => updateScan(i, 'lining', e.target.value)}
													/>
												</div>
											</div>
											<div className="col-sm-3">
												<div className="form-group">
													<label>RT</label>
													<input
														className="form-control"
														type="text"
														value={scan.right}
														onChange={e => updateScan(i, 'right', e.target.value)}
													/>
												</div>
											</div>
											<div className="col-sm-3">
												<div className="form-group">
													<label>LT</label>
													<input
														className="form-control"
														type="text"
														value={scan.left}
														onChange={e => updateScan(i, 'left', e.target.value)}
													/>
												</div>
											</div>
										</div>
									))}

									<hr />
									<h6>Semen</h6>
									<div className="row">
										<div className="col-sm-3">
											<div className="form-group">
												<label>S/A Date</label>
												<DatePicker
													className="form-control"
													value={semen.saDate ? moment(semen.saDate) : null}
													onChange={date => setSemen({ ...semen, saDate: date ? date.format('YYYY-MM-DD') : '' })}
												/>
											</div>
										</div>
										<div className="col-sm-3">
											<div className="form-group">
												<label>DS</label>
												<input
													className="form-control"
													type="text"
													value={semen.ds}
													onChange={e => setSemen({ ...semen, ds: e.target.value })}
												/>
											</div>
										</div>
										<div className="col-sm-3">
											<div className="form-group">
												<label>Code</label>
												<input
													className="form-control"
													type="text"
													value={semen.code}
													onChange={e => setSemen({ ...semen, code: e.target.value })}
												/>
											</div>
										</div>
										<div className="col-sm-3">
											<div className="form-group">
												<label>Ordered/Stored</label>
												<input
													className="form-control"
													type="text"
													value={semen.orderedStored}
													onChange={e => setSemen({ ...semen, orderedStored: e.target.value })}
												/>
											</div>
										</div>
									</div>
									<div className="row">
										<div className="col-sm-6">
											<div className="form-group">
												<label>MESA/PESA/TESA</label>
												<input
													className="form-control"
													type="text"
													value={semen.mesaPesaTesa}
													onChange={e => setSemen({ ...semen, mesaPesaTesa: e.target.value })}
												/>
											</div>
										</div>
									</div>

									<hr />
									<h6>Medication</h6>
									<div className="row">
										{[
											{ key: 'aspirin', label: 'Aspirin' },
											{ key: 'dxmPrednisolone', label: 'DXM/Prednisolone' },
											{ key: 'metformin', label: 'Metformin' },
											{ key: 'cabergolin', label: 'Cabergolin' },
										].map(med => (
											<div className="col-sm-3" key={med.key}>
												<div className="form-group">
													<label className="checkbox-inline">
														<input
															type="checkbox"
															checked={!!medications[med.key]}
															onChange={e => setMedications({ ...medications, [med.key]: e.target.checked })}
														/>{' '}
														{med.label}
													</label>
												</div>
											</div>
										))}
									</div>

									<hr />
									<h6>Consent Form</h6>
									<div className="row align-items-center">
										<div className="col-sm-3">
											<div className="form-group">
												<label className="checkbox-inline">
													<input
														type="checkbox"
														checked={!!consent.givenToPatient}
														onChange={e => setConsent({ ...consent, givenToPatient: e.target.checked })}
													/>{' '}
													Given to patient
												</label>
											</div>
										</div>
										<div className="col-sm-3">
											<div className="form-group">
												<label>Date Given</label>
												<DatePicker
													className="form-control"
													value={consent.givenDate ? moment(consent.givenDate) : null}
													onChange={date => setConsent({ ...consent, givenDate: date ? date.format('YYYY-MM-DD') : '' })}
												/>
											</div>
										</div>
										<div className="col-sm-3">
											<div className="form-group">
												<label className="checkbox-inline">
													<input
														type="checkbox"
														checked={!!consent.returnedByPatient}
														onChange={e => setConsent({ ...consent, returnedByPatient: e.target.checked })}
													/>{' '}
													Returned by patient
												</label>
											</div>
										</div>
										<div className="col-sm-3">
											<div className="form-group">
												<label>Date Returned</label>
												<DatePicker
													className="form-control"
													value={consent.returnedDate ? moment(consent.returnedDate) : null}
													onChange={date => setConsent({ ...consent, returnedDate: date ? date.format('YYYY-MM-DD') : '' })}
												/>
											</div>
										</div>
									</div>
								</FormWizard.Page>
							</FormWizard>
							{saving && <p>Saving...</p>}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MonitoringSheetDetailsForm;
